import React, { useState } from "react";
import { Box, Container, Stack } from "@mui/material";
import { Common, Custom } from "components";
import { useApp, useTheme } from "contexts";
import { Combines } from "helpers";
import { FavoriteRounded } from "@mui/icons-material";
import { RocketLaunchpad } from "./RocketLaunchpad";

type Props = {};

export const Footer = (props: Props) => {
  const { locale, t } = useApp();
  const { theme } = useTheme();

  const [isLaunching, setIsLaunching] = useState(false);

  const handleRocketLaunch = () => {
    if (isLaunching) return;
    setIsLaunching(true);

    // After 1.15s of engine ignition buildup, vibration, and smoke billowing,
    // as the rocket begins accelerating skyward, smoothly scroll back up through the atmosphere.
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 1150);

    // Reset rocket back onto the pad once the launch sequence completes
    setTimeout(() => {
      setIsLaunching(false);
    }, 4600);
  };

  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        zIndex: 2,
        width: "100%",
        mt: { xs: 24, md: 30 },
      }}
    >
      <Box
        component="style"
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes thruster-idle {
              0%, 100% {
                transform: scaleY(1) scaleX(1);
                opacity: 0.78;
              }
              50% {
                transform: scaleY(1.22) scaleX(1.08);
                opacity: 1;
              }
            }
            @keyframes cryo-vent {
              0% {
                transform: translateX(0) translateY(0) scale(0.8);
                opacity: 0.55;
              }
              100% {
                transform: translateX(-28px) translateY(-12px) scale(1.8);
                opacity: 0;
              }
            }
            @keyframes beacon-blink {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.2; }
            }
            @keyframes rocket-liftoff {
              0% { transform: translate(0, 0); }
              3% { transform: translate(-1.5px, 0.5px); }
              6% { transform: translate(1.5px, -0.5px); }
              9% { transform: translate(-2px, 0px); }
              12% { transform: translate(2px, 0.5px); }
              15% { transform: translate(-1.5px, -1px); }
              18% { transform: translate(1.5px, -2px); }
              22% { transform: translate(-1px, -6px); }
              26% { transform: translate(1px, -14px); }
              32% { transform: translate(0px, -34px); }
              40% { transform: translate(0px, -85px); }
              50% { transform: translate(0px, -200px); }
              62% { transform: translate(0px, -440px); }
              76% { transform: translate(0px, -880px); }
              90% { transform: translate(0px, -1550px); opacity: 1; }
              100% { transform: translate(0px, -2400px); opacity: 0; }
            }
            @keyframes arm-retract-1 {
              0% { transform: rotate(0deg); }
              18%, 100% { transform: rotate(-42deg); }
            }
            @keyframes arm-retract-2 {
              0% { transform: rotate(0deg); }
              20%, 100% { transform: rotate(-38deg); }
            }
            @keyframes arm-retract-3 {
              0% { transform: rotate(0deg); }
              22%, 100% { transform: rotate(-35deg); }
            }
            @keyframes smoke-billow-left {
              0% {
                transform: translate(0, 0) scale(0.4);
                opacity: 0;
              }
              15% {
                opacity: 0.92;
              }
              60% {
                opacity: 0.75;
              }
              100% {
                transform: translate(-115px, -38px) scale(2.8);
                opacity: 0;
              }
            }
            @keyframes smoke-billow-right {
              0% {
                transform: translate(0, 0) scale(0.4);
                opacity: 0;
              }
              15% {
                opacity: 0.92;
              }
              60% {
                opacity: 0.75;
              }
              100% {
                transform: translate(115px, -38px) scale(2.8);
                opacity: 0;
              }
            }
            @keyframes smoke-billow-center {
              0% {
                transform: translate(0, 0) scale(0.5);
                opacity: 0;
              }
              12% {
                opacity: 0.95;
              }
              55% {
                opacity: 0.8;
              }
              100% {
                transform: translate(0px, -65px) scale(3.4);
                opacity: 0;
              }
            }
            @keyframes afterburner-pulse {
              0%, 100% {
                transform: scaleY(1) scaleX(1);
              }
              50% {
                transform: scaleY(1.35) scaleX(1.12);
              }
            }
          `,
        }}
      />

      {/* Surface Complex 39A & Rocket Launchpad Sitting on Top of Footer */}
      <Box
        sx={{
          position: "absolute",
          bottom: "100%",
          left: 0,
          right: 0,
          height: { xs: 200, md: 240 },
          pointerEvents: "none",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          overflow: "visible",
          zIndex: 10,
        }}
      >
        {/* Ground Elevation Surface Strip */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 10,
            backgroundColor: theme.color.theme.color,
            borderTop: `2px solid ${theme.color.border.color}`,
            backgroundImage: `repeating-linear-gradient(90deg, ${theme.color.border.color} 0px, ${theme.color.border.color} 1px, transparent 1px, transparent 24px)`,
          }}
        />

        {/* Pad Elevation Telemetry & Interactive Rocket Component */}
        <Container
          maxWidth="lg"
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            pb: theme.spacing.sm,
          }}
        >
          <Box
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              alignItems: "center",
              gap: theme.spacing.xs,
              px: theme.spacing.xs,
              py: theme.spacing.xxs,
              backgroundColor: theme.color.background.accent,
              border: `1px solid ${theme.color.border.color}`,
              borderRadius: theme.border.square,
              fontFamily: '"Space Mono", monospace',
              fontSize: theme.font.xxxs,
              fontWeight: theme.font.bold,
              color: theme.color.font.accent,
              letterSpacing: "0.08em",
            }}
          >
            <span>{t.atmosphere?.pad_elevation}</span>
          </Box>

          {/* Sub-component: Interactive Rocket Launchpad */}
          <RocketLaunchpad
            isLaunching={isLaunching}
            onLaunch={handleRocketLaunch}
          />
        </Container>
      </Box>

      {/* Subterranean Launch Bunker / Footer Bottom Bar */}
      <Box
        sx={{
          backgroundColor: theme.color.theme.color,
          borderTop: `1px solid ${theme.color.border.color}`,
          boxShadow: `0 -2px 10px rgba(0,0,0,0.05)`,
          py: theme.spacing.lg,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems="center"
            justifyContent="space-between"
            spacing={theme.spacing.md}
          >
            {/* Brand & Colophon */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems="center"
              spacing={theme.spacing.sm}
              textAlign={{ xs: "center", md: "left" }}
            >
              <Box
                component="span"
                sx={{
                  display: { xs: "none", sm: "inline" },
                  color: theme.color.border.color,
                }}
              >
                •
              </Box>
              <Custom.Typography
                variant="body2"
                sx={{
                  color: theme.color.font.accent,
                  fontFamily: '"Space Mono", monospace',
                  fontSize: theme.font.xs,
                }}
              >
                {Combines.interpolate(
                  t.footer?.prefix,
                  {
                    year: new Date().toLocaleDateString(locale, {
                      year: "numeric",
                    }),
                  }
                )}{" "}
                {t.footer?.tagline}
              </Custom.Typography>
            </Stack>

            {/* Social Anchors Mini Bar */}
            <Box>
              <Common.Social size="small" />
            </Box>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};
